/**
 * Assistant-mode utilities for the chat widget.
 *
 * The chat widget can operate in multiple modes (shopping, beauty consulting,
 * and account expert). This module contains the type,
 * parsing helpers, and recognised mode list so the main index.ts stays focused
 * on orchestration.
 */

export type AssistantMode = 'shopping' | 'beauty_consulting' | 'account_expert';

const RECOGNISED_MODES: readonly AssistantMode[] = ['beauty_consulting', 'account_expert'];

/** All valid AssistantMode values (including the default 'shopping'). */
const ALL_MODES: readonly string[] = ['shopping', ...RECOGNISED_MODES];

function normalizeAssistantMode(value: string): AssistantMode | null {
  if (value === 'watch_expert') return 'account_expert';
  if (ALL_MODES.includes(value)) return value as AssistantMode;
  return null;
}

/** Return the value as an AssistantMode if it is a known mode string, otherwise null. */
export function toAssistantMode(value: unknown): AssistantMode | null {
  if (typeof value === 'string') return normalizeAssistantMode(value);
  return null;
}

/** Safely cast an unknown value to a plain Record, or null. */
export function asRecord(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/** Return the first non-empty string among the given values. */
function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length > 0) return trimmed;
    }
  }
  return undefined;
}

/**
 * Parse a redirect metadata payload and return the target assistant mode,
 * or `null` if the payload is not a valid mode-switch redirect.
 */
export function parseRedirectMode(redirectPayload: unknown): AssistantMode | null {
  const payload = asRecord(redirectPayload);
  if (!payload) return null;

  const mode = firstString(payload['assistant_mode'], payload['assistantMode']);
  if (!mode) return null;

  const normalizedMode = normalizeAssistantMode(mode);
  if (!normalizedMode || normalizedMode === 'shopping') return null;
  return normalizedMode;
}

/** Resolve the process_action type for staged image/file attachments in a mode. */
export function attachmentActionTypeForMode(mode: AssistantMode): 'user_message' | 'findSimilar' {
  return mode === 'beauty_consulting' ? 'user_message' : 'findSimilar';
}
